// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type PlacementMessageFrontendFactory,
	type FrontendStage,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState } from 'react';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import type { MessageTemplateContext } from './test-messages/jira-work-item-view-contextual-survey-jiv';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

// Contract between app and placement
export type AppProps = {
	onDismiss?: () => void;
	/**
	 * Set only by the work item view mount. Omitted from the issue view so analytics
	 * can tell the two surfaces apart without a false value on JIV events.
	 */
	isWorkItemView?: true;
};

// Contract between placement and message
export type PlacementRenderProps = AppProps;

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: `jira-issue-view-contextual-survey`,
	options: { choreographer: { isDefaultEnabled: true } },
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => ({ onDismiss, isWorkItemView }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
	const { data, error, isLoading } = usePlacementApi<{
		messages: EligibleMessage<MessageTemplateContext>[];
	}>('/');

	if (error) {
		throw error;
	}

	const visibleMessages = data?.messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		.slice(0, 1);

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds(
			(previousDismissedIds) => new Set([...Array.from(previousDismissedIds), messageInstanceId]),
		);
		onDismiss?.();
	};

	if (isLoading || !visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					{...message}
					isWorkItemView={isWorkItemView}
				/>
			))}
		</>
	);
});
